import {Component, inject, OnInit, ViewChild} from '@angular/core';
import {EditStateComponent, ItemsQuery} from "web-shared";
import {ActivatedRoute} from "@angular/router";
import {Location} from "@angular/common";
import {takeUntil, debounceTime, filter} from "rxjs/operators";
import {instanceToInstance} from "class-transformer";
import {ManufacturingItemModel} from "web-shared";
import {ManufacturingItemEditComponent} from "../manufacturing-item-edit/manufacturing-item-edit.component";
import {ManufacturingItemService} from "../manufacturing-item.service";
import {ApiResponse, GLOBAL, LeModalService, LeToastrService} from "common-shared";
import {AutoSaveService} from "../auto-save/auto-save.service";
import {StateService} from "web-shared";
import {
  ManufacturingGraphModalComponent
} from "../../graph/manufacturing-graph-modal/manufacturing-graph-modal.component";
import {ConfirmFinalModificationModalComponent} from "../../../common/confirm-final-modification-modal/confirm-final-modification-modal.component";
import {marker} from "@jsverse/transloco-keys-manager/marker";
import {GraphBuilderModalComponent} from '../../graph/graph-builder-modal/graph-builder-modal.component';

@Component({
  selector: 'mes-manufacturing-item-edit-state',
  template: `
    <app-toolbar>
      <app-breadcrumbs>
      </app-breadcrumbs>
      <app-toolbar-edit-buttons [saveState$]="saveState$" [deleteState$]="deleteState$" [showDelete]="!isNew" (save)="isNew ? add() : save()" (delete)="delete()">
        <ime-manufacturing-item-edit-menu-buttons *ngIf="!isNew"></ime-manufacturing-item-edit-menu-buttons>
      </app-toolbar-edit-buttons>
    </app-toolbar>
    <mes-manufacturing-item-edit [selectedTab]="currentTab" [item]="item" [response]="response" [shouldResetSaveState$]="shouldResetSaveState$" [wasValidated$]="validate$" [readonly]="readonly"
                                 (changed)="onChanged($event)" (setTab)="setTabInUrl($event)"/>
  `
})
export class ManufacturingItemEditStateComponent extends EditStateComponent implements OnInit {
  item: ManufacturingItemModel;
  response: ApiResponse<any>;
  currentTab: string;
  @ViewChild(ManufacturingItemEditComponent, {static: true}) editComponent: ManufacturingItemEditComponent;
  listenCacheUpdate = false;

  public stateService = inject(StateService);
  private itemsQuery = inject(ItemsQuery);
  private toastrService = inject(LeToastrService);

  constructor(private route: ActivatedRoute, protected manufacturingItemService: ManufacturingItemService, private location: Location, private autoSaveService: AutoSaveService, protected modalService: LeModalService) {
    super();
  }

  ngOnInit() {
    this.route.params.pipe(takeUntil(this.componentDestroyed)).subscribe(routeParams => {
      this.currentTab = routeParams.tab || 'general';

      this.item = this.route.snapshot.data.entity as ManufacturingItemModel;
      if (this.item == null) {
        this.isNew = true;
        this.item = ManufacturingItemModel.CreateDefault({ItemGroupId: routeParams.groupId});
      }
      this.editComponent.initialize(this.item);
      this.tryGetRestoredItem();
    });
    this.notifyIfEntityIsUpdatedElsewhere();
  }

  private tryGetRestoredItem() {
    const restoredItem = this.autoSaveService.tryGetRestoredItem(this.item.Id);
    if (restoredItem) {
      this.autoSaveService.confirmItemRestore(this.item, restoredItem).then(confirmed => {
        if (confirmed) {
          this.item = restoredItem.Content;
        }
      });
    }
  }

  delete() {
    this.manufacturingItemService.remove(this.item).pipe(takeUntil(this.componentDestroyed)).subscribe({
      next: () => {
        this.autoSaveService.removeItem(this.item.Id);
        this.router.navigate(['./manufacturing/item']).catch(reason => console.error(reason));
      }, error: value => {
        this.response = value.error;
      }
    });
  }

  save() {
    this.validate$.next(true);
    if (this.item.isValid()) {
      this.listenCacheUpdate = false;
      if (this.item.StatusClv == GLOBAL.Classificators.ItemStatus.Final) {
        this.modalService.show(ConfirmFinalModificationModalComponent).onHide$.subscribe(value => {
          if (value.result == 'save') {
            this.saveItem();
          } else if (value.result == 'create-draft') {
            this.copyAsDraft(this.item);
          }
        })
      } else {
        this.saveItem();
      }
    } else {
      this.setTabInUrl('general');
    }
  }

  saveItem = () => {
    this.manufacturingItemService.save(this.item).pipe(takeUntil(this.componentDestroyed)).subscribe({
      next: value => {
        this.updateItemAndLoadIme(value);
        this.response = null;
      }, error: value => {
        this.response = value.error;
      }
    });
  };

  add() {
    this.validate$.next(true);
    if (this.item.isValid()) {
      this.shouldResetSaveState$.next(true);
      this.manufacturingItemService.addNew(this.item).pipe(takeUntil(this.componentDestroyed)).subscribe({
        next: value => {
          this.autoSaveService.removeItem(this.item.Id);
          this.router.navigate(['./manufacturing/item/edit', value.Id]).catch(reason => console.error(reason));
        }, error: value => {
          this.response = value.error;
        }
      });
    } else {
      this.setTabInUrl('general');
    }
  }

  onChanged(isPristine: boolean) {
    this.triggerChanges(!isPristine);
    setTimeout(() => {
      this.autoSaveService.updateItem(isPristine, this.item)
    });
  }

  updateItemAndLoadIme(item: ManufacturingItemModel): void {
    this.item = instanceToInstance(item);
    this.editComponent.initialize(this.item);
    this.shouldResetSaveState$.next(true);
    this.autoSaveService.removeItem(item.Id);
  }

  setTabInUrl(tab: string) {
    if (this.currentTab !== tab) {
      let nextUrl = this.location.path().replace(this.currentTab, tab);
      this.currentTab = tab;
      this.location.go(nextUrl);
    }
  }

  showGraph() {
    this.modalService.show(ManufacturingGraphModalComponent, {initialState: {id: this.item.Id, title: this.item.DisplayName, graphSource: 'Item'} as ManufacturingGraphModalComponent, class: 'modal-full'});
  }

  showGraphBuilder() {
    this.modalService.show(GraphBuilderModalComponent, {initialState: {id: this.item.Id, title: this.item.DisplayName, graphSource: 'Item'} as GraphBuilderModalComponent, class: 'modal-full'});
  }

  copyAsDraft(item: ManufacturingItemModel = null) {
    this.manufacturingItemService.copyAsDraft(this.item.Id, item).subscribe(copiedItemId => {
      this.router.navigate(['./manufacturing/item/edit', copiedItemId], {replaceUrl: true}).catch(reason => console.error(reason));
      this.shouldResetSaveState$.next(true);
    });
  }

  publish() {
    this.manufacturingItemService.publish(this.item.Id).subscribe(itemId => {
      this.router.navigate(['./manufacturing/item/edit', itemId], {replaceUrl: true}).catch(reason => console.error(reason));
      this.shouldResetSaveState$.next(true);
    });
  }

  private notifyIfEntityIsUpdatedElsewhere() {
    this.itemsQuery.selectEntity(this.item.Id).pipe(takeUntil(this.componentDestroyed), debounceTime(1000), filter(item => {
      if (!item) return false;
      const listenCacheUpdate = this.listenCacheUpdate;
      if (!listenCacheUpdate) {
        this.listenCacheUpdate = true;
      }
      return listenCacheUpdate;
    })).subscribe(item => {
      this.toastrService.translatedWarning(marker('ime.manufacturing.manufacturing-item.Item has been updated elsewhere. Reload page'), marker('ime.manufacturing.manufacturing-item.Reload'));
    });
  }
}
